import classNames from 'classnames';
import PropTypes from 'prop-types';
import exact from 'prop-types-exact';
import React, { Component } from 'react';
import { InView } from 'react-intersection-observer';
import Player from 'react-player';

import { imagePropTypes } from '../../../propTypes';

import { withErrorBoundary } from '../Core/ErrorBoundary';
import Picture, { buildUrl } from '../Picture';

const propTypes = exact({
  videoUrl: PropTypes.string.isRequired,
  fallbackImage: imagePropTypes,
  autoplay: PropTypes.bool,
  noControls: PropTypes.bool,
});

const defaultProps = {
  fallbackImage: undefined,
  autoplay: false,
  noControls: false,
};

class VideoPlayer extends Component {
  constructor(props) {
    super(props);

    this.containerRef = React.createRef();

    this.handleButtonClick = this.handleButtonClick.bind(this);
    this.handlePlayerStop = this.handlePlayerStop.bind(this);
    this.handlePlayerError = this.handlePlayerError.bind(this);
    this.handlePlayerReady = this.handlePlayerReady.bind(this);
    this.handleInView = this.handleInView.bind(this);

    this.state = {
      isPlaying: props.autoplay,
      isError: false,
      isValidVideo: true,
      isReady: false,
      hasBeenInView: false,
    };
  }

  componentDidMount() {
    this.setState({ isValidVideo: Player.canPlay(this.props.videoUrl) });
  }

  handleButtonClick() {
    this.setState({ isPlaying: true });
  }

  handlePlayerStop() {
    this.setState({ isPlaying: false });
  }

  handlePlayerError() {
    this.setState({ isError: true });
  }

  handlePlayerReady() {
    this.setState({ isReady: true });
  }

  handleInView(inView) {
    if (!this.state.hasBeenInView && inView) {
      this.setState({
        hasBeenInView: true,
      });
    }
  }

  render() {
    const { videoUrl, fallbackImage, autoplay, noControls } = this.props;

    return (
      <InView
        className={classNames('VideoPlayer', {
          nocontrols: noControls,
        })}
        ref={this.containerRef}
        onChange={this.handleInView}
        triggerOnce
        rootMargin="200px 0px"
      >
        {this.state.hasBeenInView ? (
          <div className="VideoPlayer_frame">
            {this.state.isError || !this.state.isValidVideo ? (
              fallbackImage && (
                <Picture
                  className="VideoPlayer_fallbackImage"
                  {...fallbackImage}
                  sizes={[
                    [100, 1.77], // from 0
                    [100, 1.77], // from 440
                    [100, 1.77], // from 768
                    [100, 1.77], // from 1024
                    [100, 1.77], // from 1440
                  ]}
                />
              )
            ) : (
              <Player
                height="100%"
                config={{
                  youtube: {
                    autoplay: autoplay ? 1 : 0,
                    controls: !noControls ? 1 : 0,
                    modestBranding: 1,
                    muted: autoplay ? 1 : 0,
                  },
                  vimeo: {
                    autoplay,
                    controls: !noControls,
                    muted: autoplay,
                  },
                  file: {
                    attributes: {
                      autoplay,
                      controls: !noControls,
                      muted: autoplay,
                    },
                  },
                }}
                light={
                  !autoplay && fallbackImage && typeof fallbackImage === 'object'
                    ? buildUrl(
                        fallbackImage.url,
                        undefined,
                        this.containerRef.current ? this.containerRef.current.offsetWidth : '1550',
                        this.containerRef.current ? this.containerRef.current.offsetHeight : '876'
                      )
                    : false
                }
                loop={autoplay}
                muted={autoplay}
                onEnded={this.handlePlayerStop}
                onError={this.handlePlayerError}
                onPause={this.handlePlayerStop}
                onReady={this.handlePlayerReady}
                playIcon={
                  !autoplay ? (
                    <button
                      aria-label="Play"
                      type="button"
                      className="VideoPlayer_button"
                      onClick={this.handleButtonClick}
                    />
                  ) : undefined
                }
                playing={this.state.isPlaying}
                url={videoUrl}
                width="100%"
              />
            )}
          </div>
        ) : (
          <div className="VideoPlayer_frame" />
        )}
        {!noControls &&
          !this.state.isPlaying &&
          ((fallbackImage && this.state.isReady) || !fallbackImage) && (
            <button
              aria-label="Play"
              type="button"
              className="VideoPlayer_button"
              onClick={this.handleButtonClick}
            />
          )}
      </InView>
    );
  }
}

VideoPlayer.propTypes = propTypes;
VideoPlayer.defaultProps = defaultProps;

export default withErrorBoundary(VideoPlayer);
